import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import { CarCard } from '@/components/CarCard';
import { carsApi } from '@/lib/api';
import { useLanguage } from '@/context/LanguageContext';
import { ArrowRight } from 'lucide-react';

export default function SoldCarsPage() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, language } = useLanguage();

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const res = await carsApi.getAll({ is_sold: true });
        if (mounted) setCars(res.data);
      } catch (e) {
        console.error('Failed to load sold cars:', e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{t('sold_cars_title')} | Tim Automobile</title>
        <meta name="description" content={t('sold_cars_description')} />
      </Helmet>

      <Navbar />

      {/* Header */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <p className="overline mb-2">{t('sold_cars_subtitle')}</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light" data-testid="sold-cars-title">
            {t('sold_cars_title')}
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl">
            {t('sold_cars_description')}
          </p>
          <Link
            to="/wir-finden"
            className="inline-flex items-center gap-2 mt-6 text-gold hover:underline text-sm font-medium"
            data-testid="sold-cars-we-find-link"
          >
            {language === 'de' ? 'Wir finden Ihr Wunschauto' : 'We find your dream car'} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Cars Grid */}
      <section className="pb-24" data-testid="sold-cars-grid">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/3] bg-muted" />
                  <div className="p-6 space-y-4">
                    <div className="h-4 bg-muted w-1/4" />
                    <div className="h-6 bg-muted w-1/2" />
                    <div className="h-4 bg-muted w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : cars.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground">{t('sold_cars_empty')}</p>
            </div>
          ) : (
            <>
              <p className="text-muted-foreground mb-8">
                {cars.length} {cars.length === 1 ? t('cars_found_singular') : t('cars_found_plural')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {cars.map((car, index) => (
                  <CarCard key={car.id} car={car} index={index} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
